/**
 * Meybra Development History Timeline
 *
 * Renders the longitudinal evolution of an athlete's skills based on the
 * snapshots produced by Quick Development Updates. Each entry shows the
 * BEFORE → AFTER state (level + score + narrative) so parents and coaches
 * can read the story of how the player has grown over time.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, History, TrendingUp, TrendingDown, Minus, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_BADGE = {
  Focus: 'bg-red-500/20 text-red-400 border-red-500/30',
  Developing: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  Proficient: 'bg-green-500/20 text-green-400 border-green-500/30',
  focus: 'bg-red-500/20 text-red-400 border-red-500/30',
  developing: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  proficient: 'bg-green-500/20 text-green-400 border-green-500/30',
};

const formatLevel = (lvl) => {
  if (!lvl) return '—';
  return String(lvl).charAt(0).toUpperCase() + String(lvl).slice(1).toLowerCase();
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
};

const scoreDelta = (prev, next) => {
  if (prev == null || next == null) return null;
  const d = Number(next) - Number(prev);
  if (!Number.isFinite(d)) return null;
  return Math.round(d * 100) / 100;
};

const DeltaPill = ({ delta }) => {
  if (delta === null) return null;
  if (delta > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-green-400" data-testid="history-delta-up">
        <TrendingUp size={12} /> +{delta.toFixed(2)}
      </span>
    );
  }
  if (delta < 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-400" data-testid="history-delta-down">
        <TrendingDown size={12} /> {delta.toFixed(2)}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-muted-foreground" data-testid="history-delta-flat">
      <Minus size={12} /> 0.00
    </span>
  );
};

const DevelopmentHistory = ({ athleteId, athleteName }) => {
  const { getAuthHeaders } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [skillFilter, setSkillFilter] = useState('all');

  const load = useCallback(async () => {
    if (!athleteId) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/evaluations/athlete/${athleteId}/development-history`, {
        headers: getAuthHeaders ? getAuthHeaders() : {},
      });
      setEntries(res?.data?.entries || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load development history');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [athleteId, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const skills = useMemo(() => {
    const map = new Map();
    entries.forEach((e) => {
      if (e.skill && !map.has(e.skill)) map.set(e.skill, e.skill_name || e.skill);
    });
    return Array.from(map.entries());
  }, [entries]);

  const visible = useMemo(() => {
    if (skillFilter === 'all') return entries;
    return entries.filter((e) => e.skill === skillFilter);
  }, [entries, skillFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="dev-history-loading">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="dev-history-root">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-primary/15 flex items-center justify-center">
            <History className="text-primary" size={18} />
          </div>
          <div>
            <h3 className="font-heading text-lg uppercase tracking-wider">Development Story</h3>
            <p className="text-xs text-muted-foreground">
              {athleteName ? `${athleteName}'s ` : ''}timeline of skill evolution from coach observations.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          className="text-xs"
          data-testid="dev-history-refresh"
        >
          <RefreshCw size={14} className="mr-1" /> Refresh
        </Button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm px-3 py-2 rounded-sm" data-testid="dev-history-error">
          {error}
        </div>
      )}

      {entries.length > 0 && skills.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="dev-history-filter">
          <button
            onClick={() => setSkillFilter('all')}
            className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-wider border transition-colors ${
              skillFilter === 'all'
                ? 'bg-primary text-black border-primary'
                : 'bg-card border-border/40 text-muted-foreground hover:text-foreground'
            }`}
            data-testid="dev-history-filter-all"
          >
            All ({entries.length})
          </button>
          {skills.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setSkillFilter(key)}
              className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-wider border transition-colors ${
                skillFilter === key
                  ? 'bg-primary text-black border-primary'
                  : 'bg-card border-border/40 text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`dev-history-filter-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {visible.length === 0 && !error ? (
        <div
          className="border border-dashed border-border/40 rounded-sm p-8 text-center"
          data-testid="dev-history-empty"
        >
          <p className="text-sm text-muted-foreground">
            No development updates have been recorded yet. Submit a Quick Development Update to
            start writing this athlete&apos;s evolution story.
          </p>
        </div>
      ) : (
        <ol className="relative border-l border-border/40 pl-5 space-y-5" data-testid="dev-history-timeline">
          {visible.map((e) => {
            const delta = scoreDelta(e.prev_score, e.new_score);
            const prevLevel = formatLevel(e.prev_level);
            const newLevel = formatLevel(e.new_level);
            const levelChanged = prevLevel !== newLevel && (e.prev_level || e.new_level);
            return (
              <li key={e.id} className="relative" data-testid={`dev-history-entry-${e.id}`}>
                <span className="absolute -left-[27px] top-1.5 w-3 h-3 rounded-full bg-primary ring-4 ring-background" />
                <div className="bg-card border border-border/40 rounded-sm p-4 space-y-3">
                  <div className="flex flex-wrap items-center gap-2 justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs uppercase tracking-widest text-primary font-bold">
                        {e.skill_name || e.skill}
                      </span>
                      {levelChanged && (
                        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                          level shift
                        </span>
                      )}
                      <DeltaPill delta={delta} />
                    </div>
                    <span className="text-[11px] text-muted-foreground">
                      {formatDate(e.created_at)}
                      {e.coach_name ? ` • ${e.coach_name}` : ''}
                    </span>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="bg-muted/30 border border-border/30 rounded-sm p-3">
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Before</p>
                      <div className="flex items-center gap-2 mb-2">
                        {e.prev_level && (
                          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${LEVEL_BADGE[e.prev_level] || ''}`}>
                            {prevLevel}
                          </span>
                        )}
                        {e.prev_score != null && (
                          <span className="text-xs font-mono text-muted-foreground">
                            {Number(e.prev_score).toFixed(2)}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap">
                        {e.prev_narrative?.trim() || '— No previous narrative recorded —'}
                      </p>
                    </div>

                    <div className="bg-primary/5 border border-primary/30 rounded-sm p-3">
                      <p className="text-[10px] uppercase tracking-widest text-primary mb-1">After</p>
                      <div className="flex items-center gap-2 mb-2">
                        {e.new_level && (
                          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${LEVEL_BADGE[e.new_level] || ''}`}>
                            {newLevel}
                          </span>
                        )}
                        {e.new_score != null && (
                          <span className="text-xs font-mono text-foreground">
                            {Number(e.new_score).toFixed(2)}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-foreground leading-relaxed whitespace-pre-wrap">
                        {e.new_narrative?.trim() || '— Narrative not updated —'}
                      </p>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default DevelopmentHistory;
